import { FlexView, Row, Separator } from '@cuemath/comps';
import { Text } from '@cuemath/web-comps';
import { Table, TableCell } from 'comps';
import Pagination from 'modules/list/pagination';
import Search from 'modules/list/new-search-bar';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Menu, TableRow } from 'semantic-ui-react';
import styled from 'styled-components';

import COLORS from '../../../constants/colors';
import { useGetAllEvalFailedMappings } from 'actions/eval-failed-mappings';
import AssignCoach from './assign-coach';
import {
  MonoText,
  PageHeader,
  RangePicker,
  RegionPicker,
  TimezonePicker,
} from '../common/filters';
import { AGE_BANDS, FAILURE_REASONS, formatAge } from '../constants';
import {
  IST_ZONE,
  MAPPING_STATE_GROUPS,
  MAPPING_STATES,
  RANGE_TYPES,
  currentMonthValue,
  localInputToEpochInZone,
  rangeError,
} from '../common/constants';

const PER_PAGE = 20;

// The screen fills the viewport and never scrolls itself, so the table's own
// header can stay put; a page-level scroll takes a sticky thead with it.
const Container = styled(FlexView)`
  padding: 0 16px;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;
// Takes whatever the controls leave, measured rather than guessed at with an offset.
// height:100% as well as flex:1 -- the table's own height:100% only resolves
// against a parent with a definite height, which a bare flex item is not.
const TableWrapper = styled(Row)`
  margin-top: 16px;
  width: 100%;
  flex: 1;
  min-height: 0;
  height: 100%;

  > div {
    height: 100%;
  }
`;
const Controls = styled(FlexView)`
  flex: 0 0 auto;
  background: ${COLORS.WHITE};
  padding-bottom: 8px;
`;
const FilterFields = styled(Row)`
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;
const PaginationWrapper = styled(Row)`
  height: 42px;
`;
const SlotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;
const Slot = styled.span`
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;
const Chip = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
  white-space: nowrap;
  color: ${({ color }) => color};
  border: 1px solid ${({ color }) => color};
`;

const reasonColor = failureReason =>
  (FAILURE_REASONS[failureReason] || FAILURE_REASONS.UNKNOWN).fixable
    ? COLORS.YELLOW_4
    : COLORS.RUSTY_RED;

const stateColor = state => (MAPPING_STATES[state] || {}).color || COLORS.GRAY;

const ageColor = ageBand => {
  if (ageBand === 'BREACHED') return COLORS.RUSTY_RED;
  if (ageBand === 'AGEING') return COLORS.YELLOW_4;

  return COLORS.GRAY;
};

const TABS = [
  { key: MAPPING_STATE_GROUPS.failed, label: 'Failed' },
  { key: MAPPING_STATE_GROUPS.mapped, label: 'Mapped' },
];

const CoachMapping = () => {
  const [tab, setTab] = useState(MAPPING_STATE_GROUPS.failed);
  const [page, setPage] = useState(1);
  const [searchVal, setSearchVal] = useState('');
  const [zone, setZone] = useState(IST_ZONE);
  const [region, setRegion] = useState(null);
  const [range, setRange] = useState({
    rangeType: RANGE_TYPES.custom,
    month: currentMonthValue,
    startDate: '',
    endDate: '',
  });
  const [selectedFailure, setSelectedFailure] = useState(null);

  const {
    data,
    getAll: getFailedMappings,
    isProcessing: isLoading,
    error: queueError,
  } = useGetAllEvalFailedMappings();

  // A blank range means "use the service default window" rather than an empty result.
  const hasRange = Boolean(range.startDate && range.endDate);
  const isRangeInvalid =
    hasRange && Boolean(rangeError(range.startDate, range.endDate, true));

  const fetchQueue = useCallback(() => {
    if (isRangeInvalid) return;

    const query = {
      page,
      per_page: PER_PAGE,
      search: searchVal,
      mapping_state: tab,
      ...(region ? { region_code: region } : {}),
      // Epochs, not dates: the window is an instant pair, and the epoch already
      // carries the zone so the service never has to guess a day boundary.
      ...(hasRange
        ? {
            start_epoch: localInputToEpochInZone(range.startDate, zone),
            end_epoch: localInputToEpochInZone(range.endDate, zone),
          }
        : {}),
    };

    if (!searchVal) delete query.search;
    getFailedMappings(query);
  }, [page, searchVal, tab, range, zone, region, hasRange, isRangeInvalid]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handlePageChange = useCallback(newPage => {
    setPage(newPage);
  }, []);

  const handleSearch = useCallback(val => {
    setPage(1);
    setSearchVal(val);
  }, []);

  const handleTabChange = useCallback(nextTab => {
    setPage(1);
    setTab(nextTab);
  }, []);

  const handleRegionChange = useCallback(nextRegion => {
    setPage(1);
    setRegion(nextRegion);
  }, []);

  const queueErrorText = queueError?.message || (queueError ? String(queueError) : null);
  const results = data?.results || [];
  const breachedCount = data?.breached_count || 0;
  const showPagination = results.length > 0;
  const isFailedTab = tab === MAPPING_STATE_GROUPS.failed;

  return (
    <Container>
      <PageHeader title="Coach Mapping" />
      <Controls>
      <Menu pointing secondary>
        {TABS.map(entry => (
          <Menu.Item
            key={entry.key}
            name={entry.label}
            active={tab === entry.key}
            onClick={() => handleTabChange(entry.key)}
          />
        ))}
      </Menu>
      <Row justifyContent="space-between" alignItems="center">
        <FilterFields gutter={0}>
          <RangePicker value={range} onChange={setRange} showMonth={false} withTime />
          <RegionPicker value={region} onChange={handleRegionChange} />
          <TimezonePicker value={zone} onChange={setZone} region={region} />
        </FilterFields>
        <Search
          showNewSearchBar={true}
          onSearch={handleSearch}
          placeholder="Search by enrollment ID"
        />
      </Row>
      <Row justifyContent="space-between" alignItems="center">
        <Text
          variant="body1"
          color={isFailedTab && breachedCount ? COLORS.RUSTY_RED : COLORS.GRAY}
        >
          {isFailedTab
            ? breachedCount
              ? `${breachedCount} student${
                  breachedCount === 1 ? '' : 's'
                } waiting over 24h`
              : 'No student waiting over 24h'
            : `${data?.total ?? 0} student${
                (data?.total ?? 0) === 1 ? '' : 's'
              } with a coach`}
        </Text>
      </Row>
      </Controls>

      {queueErrorText && (
        <Row>
          <Text variant="body2" color={COLORS.RUSTY_RED}>
            {queueErrorText}
          </Text>
        </Row>
      )}

      <TableWrapper>
        <Table
          headerCell={[
            { title: 'Student' },
            { title: 'Grade' },
            { title: 'Region' },
            { title: isFailedTab ? 'Waiting' : 'Mapped' },
            { title: 'Slots (IST)' },
            ...(isFailedTab
              ? [{ title: 'Attempts' }, { title: 'Reason' }]
              : [{ title: 'Coach' }, { title: 'State' }]),
            { title: '' },
          ]}
          dataLength={results.length}
          havePagination
          showInfo={false}
          showLoader={isLoading}
          flexBasis="100%"
          noDataMessage={
            isFailedTab
              ? 'No failed mappings — every eval student has a coach.'
              : 'No mapped students in this window.'
          }
          scrollBodyHeight={isLoading || !showPagination ? '400px' : '100%'}
        >
          {results.map(failure => (
            <TableRow key={failure.class_mapping_request_id}>
              <TableCell width={4} textTransform="none">
                {failure.intel_enrollment_id}
              </TableCell>
              <TableCell width={1}>{failure.grade || '-'}</TableCell>
              <TableCell width={1}>{failure.region_code || '-'}</TableCell>
              <TableCell width={2}>
                {/* On a mapped row this age is time since the decision, so the
                    breach colouring would read as a problem that is already solved. */}
                <Chip color={isFailedTab ? ageColor(failure.age_band) : COLORS.GRAY}>
                  {formatAge(failure.age_hours)}
                  {isFailedTab && failure.age_band === 'BREACHED'
                    ? ` · ${AGE_BANDS.BREACHED.label}`
                    : ''}
                  {isFailedTab ? '' : ' ago'}
                </Chip>
              </TableCell>
              <TableCell width={3} textTransform="none">
                {(failure.requested_slots_ist || []).length ? (
                  <SlotList>
                    {(failure.requested_slots_ist || []).map(slot => (
                      <Slot key={slot}>{slot}</Slot>
                    ))}
                  </SlotList>
                ) : (
                  '-'
                )}
              </TableCell>
              {isFailedTab ? (
                <>
                  <TableCell width={1}>{failure.attempt_count}</TableCell>
                  <TableCell width={3} textTransform="none">
                    <Chip color={reasonColor(failure.failure_reason)}>
                      {
                        (
                          FAILURE_REASONS[failure.failure_reason] ||
                          FAILURE_REASONS.UNKNOWN
                        ).label
                      }
                    </Chip>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell width={4} textTransform="none">
                    {failure.assigned_teacher_name || '-'}
                    <Separator transparent height={2} />
                    <MonoText>{failure.assigned_teacher_id || '-'}</MonoText>
                  </TableCell>
                  <TableCell width={2} textTransform="none">
                    <Chip color={stateColor(failure.state)}>
                      {(MAPPING_STATES[failure.state] || {}).label || failure.state}
                    </Chip>
                    {failure.is_manual && (
                      <>
                        <Separator transparent height={2} />
                        <Text variant="body3" color={COLORS.GRAY}>
                          by hand
                        </Text>
                      </>
                    )}
                  </TableCell>
                </>
              )}
              <TableCell width={2}>
                <Button
                  size="tiny"
                  color="black"
                  disabled={!isFailedTab && failure.is_enrolled}
                  onClick={() => setSelectedFailure(failure)}
                >
                  {isFailedTab ? 'Assign' : 'Change coach'}
                </Button>
                {!isFailedTab && failure.is_enrolled && (
                  <>
                    <Separator transparent height={2} />
                    <Text variant="body3" color={COLORS.GRAY}>
                      already enrolled
                    </Text>
                  </>
                )}
              </TableCell>
            </TableRow>
          ))}
        </Table>
      </TableWrapper>

      <Separator transparent height={8} />
      <PaginationWrapper justifyContent="center" alignItems="center">
        {showPagination && (
          <Pagination
            page={page}
            perPage={PER_PAGE}
            count={data?.total ?? null}
            onPageChange={handlePageChange}
            shouldShowNewUi
          />
        )}
      </PaginationWrapper>

      {selectedFailure && (
        <AssignCoach
          failure={selectedFailure}
          closeModal={() => setSelectedFailure(null)}
          onAssigned={fetchQueue}
        />
      )}
    </Container>
  );
};

export default CoachMapping;
